import { useState, useEffect } from 'react'
import MobileLayout from '../../components/MobileLayout'
import { employeeApi } from '../../lib/employeeApi'

interface AbsenceType { id: string; name: string; code: string; color: string }
interface Absence {
  id: string; startDate: string; endDate: string; durationDays: number
  status: string; note?: string; modificationNote?: string
  absenceType: { name: string; color: string; isPaid: boolean }
  createdAt: string
}
interface Balance {
  id: string; acquired: number; taken: number; anticipated: number; allowNegative: boolean
  absenceType: { id: string; name: string; code: string; color: string }
}

const STATUS_LABELS: Record<string, string> = {
  pending:                'En attente',
  approved:               'Approuvé',
  refused:                'Refusé',
  cancelled:              'Annulé',
  modification_requested: 'Modif. demandée'
}
const STATUS_COLORS: Record<string, string> = {
  pending:                'bg-yellow-100 text-yellow-700',
  approved:               'bg-green-100 text-green-700',
  refused:                'bg-red-100 text-red-700',
  cancelled:              'bg-gray-100 text-gray-500',
  modification_requested: 'bg-purple-100 text-purple-700'
}

function fmt(d: string) {
  return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function MobileAbsences() {
  const [absences, setAbsences] = useState<Absence[]>([])
  const [types, setTypes] = useState<AbsenceType[]>([])
  const [balances, setBalances] = useState<Balance[]>([])
  const [showForm, setShowForm] = useState(false)
  const [showBalances, setShowBalances] = useState(false)
  const [form, setForm] = useState({ absenceTypeId: '', startDate: '', endDate: '', note: '' })
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    employeeApi.get('/employee/me/absences').then(r => setAbsences(r.data))
    employeeApi.get('/employee/absence-types').then(r => setTypes(r.data))
    employeeApi.get(`/employee/me/balances`).then(r => setBalances(r.data)).catch(() => {})
  }, [])

  function calcDays() {
    if (!form.startDate || !form.endDate) return 0
    const start = new Date(form.startDate + 'T00:00:00')
    const end = new Date(form.endDate + 'T00:00:00')
    let count = 0
    const cur = new Date(start)
    while (cur <= end) { const d = cur.getDay(); if (d !== 0 && d !== 6) count++; cur.setDate(cur.getDate() + 1) }
    return count || 1
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    if (!form.absenceTypeId || !form.startDate || !form.endDate) { setError('Remplissez tous les champs obligatoires'); return }
    setSubmitting(true)
    try {
      await employeeApi.post('/employee/me/absences', { ...form, durationDays: calcDays() })
      const r = await employeeApi.get('/employee/me/absences')
      setAbsences(r.data)
      setShowForm(false)
      setForm({ absenceTypeId: '', startDate: '', endDate: '', note: '' })
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur lors de la demande')
    } finally { setSubmitting(false) }
  }

  const inputCls = 'w-full border border-gray-200 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300'
  const balancesWithData = balances.filter(b => b.acquired > 0 || b.taken > 0 || b.anticipated > 0)

  return (
    <MobileLayout>
      <div className="p-4 space-y-4">

        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-800">Congés & Absences</h1>
          <button onClick={() => setShowForm(v => !v)}
            className="bg-blue-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-blue-600 transition-colors">
            + Demander
          </button>
        </div>

        {/* Balances / soldes */}
        {balancesWithData.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <button onClick={() => setShowBalances(v => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700">
              <span>Mes soldes de congés</span>
              <span className="text-gray-400 text-lg">{showBalances ? '▲' : '▼'}</span>
            </button>
            {showBalances && (
              <div className="border-t border-gray-100">
                {balancesWithData.map(b => {
                  const remaining = b.acquired + b.anticipated - b.taken
                  const pct = b.acquired > 0 ? Math.max(0, Math.min(100, (remaining / b.acquired) * 100)) : 0
                  return (
                    <div key={b.id} className="px-4 py-3 border-b border-gray-50 last:border-0">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: b.absenceType.color }} />
                          <span className="text-sm font-medium text-gray-700">{b.absenceType.name}</span>
                        </div>
                        <span className={`text-sm font-bold ${remaining < 0 ? 'text-red-600' : remaining < 3 ? 'text-orange-500' : 'text-green-600'}`}>
                          {remaining.toFixed(1)}j restants
                        </span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div className="h-1.5 rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: b.absenceType.color }} />
                      </div>
                      <div className="flex justify-between text-xs text-gray-400 mt-1">
                        <span>{b.taken.toFixed(1)}j pris</span>
                        <span>{b.acquired.toFixed(1)}j acquis{b.anticipated > 0 ? ` + ${b.anticipated.toFixed(1)}j anticipés` : ''}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* Request form */}
        {showForm && (
          <div className="bg-white rounded-2xl shadow-sm p-4">
            <h2 className="font-semibold text-gray-800 mb-4">Nouvelle demande</h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Type d'absence *</label>
                <select value={form.absenceTypeId} onChange={e => setForm(f => ({ ...f, absenceTypeId: e.target.value }))} className={inputCls}>
                  <option value="">Choisir...</option>
                  {types.map(t => <option key={t.id} value={t.id}>{t.name} ({t.code})</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Début *</label>
                  <input type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} className={inputCls} />
                </div>
                <div>
                  <label className="text-sm text-gray-600 mb-1 block">Fin *</label>
                  <input type="date" value={form.endDate} min={form.startDate} onChange={e => setForm(f => ({ ...f, endDate: e.target.value }))} className={inputCls} />
                </div>
              </div>
              {form.startDate && form.endDate && (
                <div className="text-sm text-blue-600 font-medium">{calcDays()} jour(s) ouvré(s)</div>
              )}
              <div>
                <label className="text-sm text-gray-600 mb-1 block">Note (optionnel)</label>
                <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                  rows={2} placeholder="Précisions..." className={`${inputCls} resize-none`} />
              </div>
              {error && <p className="text-red-500 text-sm">{error}</p>}
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowForm(false)}
                  className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-medium text-sm">Annuler</button>
                <button type="submit" disabled={submitting}
                  className="flex-1 py-3 rounded-xl bg-blue-500 text-white font-medium text-sm disabled:opacity-50">
                  {submitting ? 'Envoi...' : 'Envoyer'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Absence list */}
        <div className="space-y-2">
          {absences.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center text-gray-400 shadow-sm">Aucune demande d'absence</div>
          ) : absences.map(a => (
            <div key={a.id} className="bg-white rounded-2xl shadow-sm p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <span className="inline-block text-xs font-medium px-2 py-0.5 rounded-full text-white mb-1"
                    style={{ backgroundColor: a.absenceType.color }}>
                    {a.absenceType.name}
                  </span>
                  <div className="text-sm text-gray-700 font-medium">{fmt(a.startDate)} → {fmt(a.endDate)}</div>
                  <div className="text-xs text-gray-500">{a.durationDays} jour(s){a.absenceType.isPaid ? '' : ' · Non payé'}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-600'}`}>
                  {STATUS_LABELS[a.status] || a.status}
                </span>
              </div>
              {a.note && <p className="text-xs text-gray-400 italic">{a.note}</p>}
              {a.modificationNote && (
                <p className="text-xs text-purple-600 mt-1 bg-purple-50 px-2 py-1 rounded">
                  Modification demandée : {a.modificationNote}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </MobileLayout>
  )
}
